import { useMemo } from "react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/authStore";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AGENT_PULSE_GENERATION_DAY,
  type AgentPulseUpdate,
  DAY_NAMES,
} from "@forgepoint/shared";
import { JournalEntry } from "./JournalEntry";

/** Day the memo is generated, kept in sync with the cron schedule constant. */
const GENERATION_DAY = DAY_NAMES[AGENT_PULSE_GENERATION_DAY];

interface WeeklyJournalProps {
  latestUpdate: AgentPulseUpdate | null;
  historicalUpdates: AgentPulseUpdate[];
  selectedUpdateId: string | null;
  onSelectUpdate: (id: string) => void;
  isLoading: boolean;
}

/**
 * Displays the agent's weekly journal styled as an old-school typed memo,
 * with a FROM/TO/DATE/RE header and a monospace body. Includes a dropdown
 * to browse historical entries.
 * @param props - Journal data, selection state, and loading flag
 * @returns The weekly journal memo card
 */
export function WeeklyJournal({
  latestUpdate,
  historicalUpdates,
  selectedUpdateId,
  onSelectUpdate,
  isLoading,
}: WeeklyJournalProps) {
  const agentName = useStandardAgentDisplayName();
  const orgName = useAuthStore((s) => s.user?.organization.name);

  const selectedUpdate = useMemo(() => {
    if (!selectedUpdateId) {
      return latestUpdate;
    }
    return (
      historicalUpdates.find((u) => u.id === selectedUpdateId) ?? latestUpdate
    );
  }, [selectedUpdateId, historicalUpdates, latestUpdate]);

  const dateRange = selectedUpdate
    ? formatMemoDate(selectedUpdate.weekStartDate, selectedUpdate.weekEndDate)
    : "";

  return (
    <div className="min-w-0">
      <div className="rounded-xl border bg-card shadow-sm">
        {/* Card header — title, badge, week picker */}
        <div className="flex items-center gap-2.5 border-b px-6 py-4">
          <h2 className="text-lg font-semibold tracking-tight">
            {agentName}&apos;s Weekly Memo
          </h2>
          {historicalUpdates.length > 0 && (
            <div className="ml-auto">
              <Select
                value={selectedUpdateId ?? latestUpdate?.id ?? ""}
                onValueChange={onSelectUpdate}
              >
                <SelectTrigger className="h-8 w-[200px] font-mono text-xs">
                  <SelectValue placeholder="Select week" />
                </SelectTrigger>
                <SelectContent>
                  {historicalUpdates.map((update) => (
                    <SelectItem key={update.id} value={update.id}>
                      {formatWeekRange(
                        update.weekStartDate,
                        update.weekEndDate,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Memo content */}
        <div className="px-7 py-6 sm:px-8">
          {isLoading ? (
            <LoadingSkeleton />
          ) : !selectedUpdate ? (
            <EmptyState />
          ) : (
            <>
              {/* FROM / DATE / TO header */}
              <div className="flex justify-between font-mono text-[12px] leading-relaxed">
                <div className="flex flex-col gap-1">
                  <p>
                    <span className="font-bold uppercase">From:</span>{" "}
                    <span className="uppercase">{agentName}</span>
                  </p>
                  <p>
                    <span className="font-bold uppercase">To:</span>{" "}
                    <span className="uppercase">{orgName ?? "—"}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p>
                    <span className="font-bold uppercase">Date:</span>{" "}
                    <span className="uppercase">{dateRange}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground/70">
                    Updated weekly on {GENERATION_DAY}s
                  </p>
                </div>
              </div>

              {/* Dotted separator */}
              <div className="mt-4 border-t border-dashed border-foreground/20" />

              {/* Memo body */}
              <div className="mt-6">
                <JournalEntry content={selectedUpdate.content} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Formats start/end dates for the memo DATE header field.
 * @param start - Week start date
 * @param end - Week end date
 * @returns Formatted string like "May 24 — May 30"
 */
function formatMemoDate(start: Date, end: Date): string {
  const startDisplay = format(
    new Date(
      start.getUTCFullYear(),
      start.getUTCMonth(),
      start.getUTCDate(),
      12,
    ),
    "MMM d",
  );
  const endDisplay = format(
    new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 12),
    "MMM d",
  );
  return `${startDisplay} — ${endDisplay}`;
}

/**
 * Formats a week date range for display in the history dropdown.
 * @param start - Week start date
 * @param end - Week end date
 * @returns Formatted string like "May 25 – Jun 1"
 */
function formatWeekRange(start: Date, end: Date): string {
  const startDisplay = format(
    new Date(
      start.getUTCFullYear(),
      start.getUTCMonth(),
      start.getUTCDate(),
      12,
    ),
    "MMM d",
  );
  const endDisplay = format(
    new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 12),
    "MMM d",
  );
  return `${startDisplay} – ${endDisplay}`;
}

/**
 * Empty state shown when no journal entries exist yet.
 * @returns Placeholder message for first-time users
 */
function EmptyState() {
  return (
    <div className="py-12 text-center font-mono">
      <p className="text-sm font-medium text-foreground/70">No entries yet</p>
      <p className="mt-1 text-xs text-muted-foreground">
        The first memo will be generated {GENERATION_DAY} morning, covering the
        week that closed the night before.
      </p>
    </div>
  );
}

/**
 * Loading skeleton matching the memo layout.
 * @returns Skeleton placeholder while data loads
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="mx-auto h-3 w-20" />
      <Skeleton className="h-px w-full" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-36" />
        <Skeleton className="h-3 w-44" />
        <Skeleton className="h-3 w-48" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-2.5 pt-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}
